'use client'

import { cn } from '@/lib/utils'
import { Typo3Image, Typo3Video } from '@/types/typo3'
import { useEffect, useRef, useState } from 'react'
import Button from '../Button/Button'
import Img from '../Img/Img'

interface CardTeamProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  name?: string
  role?: string | null
  image?: Typo3Image
  email?: string
  phone?: string
  link?: string
  hoverImage?: Typo3Image
  hoverVideo?: Typo3Video
}

const CardTeam = ({
  name,
  role,
  image,
  email,
  phone,
  link,
  hoverImage,
  hoverVideo,
  className,
  ...props
}: CardTeamProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showHover, setShowHover] = useState(false)

  useEffect(() => {
    if (!videoRef.current) {
      return
    }

    if (showHover) {
      videoRef.current.play()
    } else {
      videoRef.current?.pause()
      videoRef.current.currentTime = 0
    }
  }, [showHover, videoRef])

  return (
    <div
      className={cn(
        'group flex flex-col bg-neutral-200 text-neutral-600',
        className
      )}
      onClick={() => {
        setShowHover(!showHover)
      }}
      onMouseEnter={() => {
        setShowHover(true)
      }}
      onMouseLeave={() => {
        setShowHover(false)
      }}
      {...props}
    >
      <div className="p-md pb-0">
        <div className="relative aspect-square overflow-hidden">
          {image && (
            <Img
              className="h-full w-full"
              data={image}
              sizes={`25rem`}
              objectFit
              fill
            />
          )}
          <div
            data-show-hover={showHover ? 'true' : 'false'}
            className="absolute inset-0 opacity-0 transition-opacity duration-300 data-[show-hover='true']:opacity-100"
          >
            {hoverImage && (
              <Img
                className="h-full w-full"
                data={hoverImage}
                sizes={`25rem`}
                objectFit
                fill
              />
            )}
            {hoverVideo && (
              <video
                className="h-full w-full object-cover"
                autoPlay
                muted
                loop
                ref={videoRef}
                playsInline
              >
                <source
                  src={hoverVideo.publicUrl}
                  type={hoverVideo.properties?.mimeType || 'video/mp4'}
                />
              </video>
            )}
          </div>
        </div>
      </div>
      <div className="p-md gap-md flex flex-col">
        <div className="">
          {name && <p className="textstyle-lg-bold">{name}</p>}
          {role && (
            <p className="textstyle-md-regular text-neutral-400">{role}</p>
          )}
        </div>
        {(email || phone || link) && (
          <div className="gap-y-xs flex flex-col items-start">
            {email && (
              <Button
                icon="material-symbols:mail-sharp"
                variant="text"
                iconPosition="left"
                href={`mailto:${email}`}
                size="m"
                iconClassName="text-accent-300"
              >
                {email}
              </Button>
            )}
            {phone && (
              <Button
                icon="material-symbols:phone-enabled-sharp"
                variant="text"
                href={`tel:${phone}`}
                iconPosition="left"
                size="m"
                iconClassName="text-accent-300"
              >
                {phone}
              </Button>
            )}
            {link && (
              <Button
                variant="text"
                href={link}
                size="m"
                iconClassName="text-accent-300"
              >
                {link}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

CardTeam.displayName = 'CardTeam'

export default CardTeam
